import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";

const STATUSES = ["healthy", "watchlist", "high_risk", "suspicious", "disqualified"];

export default function RiskMonitoring() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [risk, setRisk] = useState("healthy");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status = filter;
      const { data } = await api.get("/risk", { params });
      setRows(data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    if (!target) return;
    try {
      await api.post(`/risk/${target.id}`, { risk_status: risk, risk_reason: reason, notes });
      toast.success("Risk updated");
      setTarget(null); setReason(""); setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader title="Risk Monitoring" subtitle="Flag anomalies, watchlist suspicious activity, disqualify rule-breakers." breadcrumbs="Operations / Risk" testId="risk-page" />

      <div className="flex gap-2 mb-4">
        {["", ...STATUSES].map((s) => (
          <button key={s || "all"} data-testid={`risk-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs rounded-md border ${filter === s ? "bg-blue-500/10 border-blue-500 text-blue-300" : "border-white/10 text-gray-400 hover:text-white"}`}>
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3 text-right">Valid Lot</th>
                <th className="px-4 py-3">Risk</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-500">No participants</td></tr> :
                rows.map((p) => (
                  <tr key={p.id} data-testid={`risk-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-white">{p.full_name}</p>
                      <p className="text-xs font-mono text-gray-500">{p.broker_account_number}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white">{(p.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.risk_status || "healthy"} /></td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-xs truncate">{p.notes || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`risk-edit-${p.id}`} onClick={() => { setTarget(p); setRisk(p.risk_status || "healthy"); }} className="px-3 py-1 text-xs bg-orange-500/10 border border-orange-500/30 text-orange-300 rounded-md inline-flex items-center gap-1 hover:bg-orange-500/20">
                        <Warning size={12} /> Flag
                      </button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Update Risk · {target?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1.5">New risk status</p>
              <select data-testid="risk-status-select" value={risk} onChange={(e) => setRisk(e.target.value)} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
            <input data-testid="risk-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g., sudden lot spike)" className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
            <textarea data-testid="risk-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" rows={3} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setTarget(null)} className="px-4 py-2 text-sm text-gray-400">Cancel</button>
            <button data-testid="risk-submit" onClick={submit} className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md">Update</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
